import React from 'react';
import PropTypes from 'prop-types';

import './TopicBadge.scss';

const propTypes = {

	/**
	 * The topic ID.
	 */
	id: PropTypes.string.isRequired,

	/**
	 * The topic title.
	 */
	title: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.func,
		PropTypes.element,
		PropTypes.node,
	]).isRequired,

	/**
	 * True, if the topic is active (used by HTML button/link).
	 */
	active: PropTypes.bool.isRequired,

	/**
	 * The custom CSS classes (if any) to add to the topic badge.
	 */
	className: PropTypes.string,

	/**
	 * ReactJS ref for the topic badge.
	 */
	innerRef: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.shape({}),
	]),

	/**
	 * Optional ReactJS element tag that can be used to override
	 * the default tag for the topic badge (i.e. HTML span element).
	 */
	tag: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.func,
		PropTypes.element,
		PropTypes.node,
	]),

};

const defaultProps = {
	active: false,
};

/**
 * Shared component to render a TechChange topic badge.
 *
 * @param {Object} props  The component props.
 *
 * @returns {Object}      The TopicBadge component.
 */
const TopicBadge = (props) => {

	const {
		id,
		title,
		tag,
		active,
		className,
		innerRef,
		...restProps
	} = props;

	const baseProps = {
		ref: innerRef,
		id,
		// Topic ID included for CSS overrides
		className: `tc-topic-badge rounded-pill topic-${id} ${className || ''}`,
		title,
	};

	let badgeContent;

	// Check if overriding the HTML badge (i.e. span) with a custom tag
	if (tag) {

		if (typeof tag === 'string') {

			if (tag === 'button') {

				baseProps.className +=
					` btn ${active ? 'btn-primary' : 'btn-outline-neutral'}`;

			} else {

				baseProps.className +=
					` badge ${active ? 'bg-primary' : 'bg-neutral'}`;

			}

			badgeContent = React.createElement(
				tag,
				Object.assign({}, restProps, baseProps),
				title,
			);

		} else {

			baseProps.className +=
				` badge ${active ? 'bg-primary' : 'bg-neutral'}`;

			badgeContent = React.cloneElement(
				tag,
				Object.assign({}, restProps, baseProps),
				title,
			);

		}

	// Otherwise, render as the default HTML badge
	} else {

		baseProps.className += ' badge bg-neutral';

		badgeContent = (
			<span
				{...restProps}
				{...baseProps}>
				{title}
			</span>
		);

	}

	return (
		<>{badgeContent}</>
	);

};

TopicBadge.defaultProps = defaultProps;
TopicBadge.propTypes = propTypes;

export default TopicBadge;
